import React, { useCallback } from 'react';
import type { MessengerMessage } from '@/api/client';
import type { DirectDmThreadRowBleed } from '@/components/Messenger/direct/directDmThreadRowLayout';
import './DirectMessageSelectableRow.css';

interface DirectMessageSelectableRowProps {
  msg: MessengerMessage;
  isOwn: boolean;
  selectionMode: boolean;
  isSelected: boolean;
  onToggleSelect: (msg: MessengerMessage) => void;
  /** Родитель ленты: компенсация внутренних отрицательных margin бабла + сохранение шага ленты */
  rowBleed: DirectDmThreadRowBleed;
  /** Подтянуть бабл к предыдущему от того же отправителя (раньше — класс на MessageRow внутри бабла) */
  stackWithPrev: boolean;
  /** Двойной клик по баблу (не в режиме выбора): лайк */
  onToggleMessageLike?: (msg: MessengerMessage) => void;
  children: React.ReactNode;
}

function isInteractiveBubbleTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest(
      'button, a, input, textarea, select, [role="menuitem"], [data-dm-no-select-toggle]'
    )
  );
}

export const DirectMessageSelectableRow = React.memo(function DirectMessageSelectableRow({
  msg,
  isOwn,
  selectionMode,
  isSelected,
  onToggleSelect,
  rowBleed,
  stackWithPrev,
  onToggleMessageLike,
  children,
}: DirectMessageSelectableRowProps) {
  const selectionApplies = selectionMode && msg.message_kind !== 'system';

  const toggle = useCallback(() => {
    onToggleSelect(msg);
  }, [msg, onToggleSelect]);

  const handleBubbleAreaClick = useCallback(
    (e: React.MouseEvent) => {
      if (!selectionApplies) return;
      if (isInteractiveBubbleTarget(e.target)) return;
      toggle();
    },
    [selectionApplies, toggle]
  );

  const handleBubbleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (selectionMode) return;
      if (msg.message_kind === 'system') return;
      if (isInteractiveBubbleTarget(e.target)) return;
      if (!onToggleMessageLike) return;
      if (msg.deleted_at) return;
      onToggleMessageLike(msg);
    },
    [selectionMode, onToggleMessageLike, msg]
  );

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      toggle();
    },
    [toggle]
  );

  const checkCell = selectionApplies ? (
    <div className="directThreadView__msgCheckCell">
      <label className="directThreadView__msgCheckLabel">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleCheckboxChange}
          aria-label={isSelected ? 'Снять выбор с сообщения' : 'Выбрать сообщение'}
        />
      </label>
    </div>
  ) : null;

  return (
    <div
      className={[
        'directThreadView__msgSelectRow',
        isOwn ? 'directThreadView__msgSelectRow_own' : '',
        selectionApplies ? 'directThreadView__msgSelectRow_modeSelection' : '',
        rowBleed === 'grouped' ? 'directThreadView__msgSelectRow_bleed_grouped' : '',
        rowBleed === 'flushImage' ? 'directThreadView__msgSelectRow_bleed_flushImage' : '',
        isSelected ? 'directThreadView__msgSelectRow_selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {!isOwn ? checkCell : null}
      <div
        className={[
          'directThreadView__msgBubbleCell',
          selectionMode ? 'directThreadView__msgBubbleCell_selectable' : '',
          stackWithPrev ? 'directThreadView__msgBubbleCell_stackPrev' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={handleBubbleAreaClick}
        onDoubleClick={handleBubbleDoubleClick}
      >
        {children}
      </div>
      {isOwn ? checkCell : null}
    </div>
  );
});
